/* --------------------------------------------------------------
 * Gallery grid lightbox.
 *
 * Every `[data-gallery-grid]` root initialises itself, so multiple
 * instances on a page (and the theme editor's section reloads) are safe.
 * Tiles carry the full-size image data as data-attributes; this file only
 * swaps them into the single per-section lightbox dialog.
 * ------------------------------------------------------------ */
(function () {
  'use strict';

  // The section renders one <script src> per instance; only define once.
  if (window.XenonGalleryGrid) {
    window.XenonGalleryGrid.initAll();
    return;
  }

  var OPEN_CLASS = 'gallery-lightbox-open';
  var instances = [];
  var openInstance = null;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function setText(el, value) {
    if (!el) return;
    el.textContent = value || '';
    el.hidden = !value;
  }

  function GalleryGrid(root) {
    var lightbox = root.querySelector('[data-gallery-lightbox]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-item]'));
    if (!lightbox || !items.length) return null;

    var image = lightbox.querySelector('[data-gallery-lb-image]');
    var titleEl = lightbox.querySelector('[data-gallery-lb-title]');
    var textEl = lightbox.querySelector('[data-gallery-lb-text]');
    var captionEl = lightbox.querySelector('[data-gallery-lb-caption]');
    var currentEl = lightbox.querySelector('[data-gallery-lb-current]');
    var totalEl = lightbox.querySelector('[data-gallery-lb-total]');
    var closeBtn = lightbox.querySelector('[data-gallery-close]');
    var prevBtn = lightbox.querySelector('[data-gallery-prev]');
    var nextBtn = lightbox.querySelector('[data-gallery-next]');

    var loop = root.getAttribute('data-gallery-loop') === 'true';
    var index = 0;
    var isOpen = false;
    var lastFocused = null;
    var touchStartX = 0;
    var touchStartY = 0;

    if (totalEl) totalEl.textContent = pad(items.length);

    function onImageLoad() {
      lightbox.classList.add('is-loaded');
    }

    function preload(i) {
      var el = items[i];
      if (!el) return;
      var src = el.getAttribute('data-full-src');
      if (!src) return;
      var pre = new Image();
      var srcset = el.getAttribute('data-full-srcset');
      if (srcset) pre.srcset = srcset;
      pre.src = src;
    }

    function show(i) {
      if (!items.length) return;

      if (i < 0) i = loop ? items.length - 1 : 0;
      if (i > items.length - 1) i = loop ? 0 : items.length - 1;

      index = i;
      var el = items[index];

      lightbox.classList.remove('is-loaded');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');

      var width = parseInt(el.getAttribute('data-width'), 10);
      var height = parseInt(el.getAttribute('data-height'), 10);
      if (width > 0 && height > 0) {
        image.setAttribute('width', width);
        image.setAttribute('height', height);
      }

      image.alt = el.getAttribute('data-alt') || '';
      var srcset = el.getAttribute('data-full-srcset');
      if (srcset) {
        image.setAttribute('srcset', srcset);
        image.setAttribute('sizes', '100vw');
      }
      image.src = el.getAttribute('data-full-src') || '';
      if (image.complete) onImageLoad();

      var title = el.getAttribute('data-title');
      var caption = el.getAttribute('data-caption');
      setText(titleEl, title);
      setText(textEl, caption);
      if (captionEl) captionEl.hidden = !title && !caption;

      if (currentEl) currentEl.textContent = pad(index + 1);

      if (!loop) {
        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index === items.length - 1;
      }

      preload(index + 1);
      preload(index - 1);
    }

    function open(i) {
      if (isOpen) return;
      lastFocused = document.activeElement;
      isOpen = true;
      openInstance = api;
      show(i);
      lightbox.setAttribute('data-open', 'true');
      lightbox.removeAttribute('aria-hidden');
      lightbox.removeAttribute('inert');
      document.documentElement.classList.add(OPEN_CLASS);
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      openInstance = null;
      lightbox.setAttribute('data-open', 'false');
      lightbox.setAttribute('aria-hidden', 'true');
      lightbox.setAttribute('inert', '');
      lightbox.classList.remove('is-loaded');
      document.documentElement.classList.remove(OPEN_CLASS);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      lastFocused = null;
    }

    function step(delta) {
      show(index + delta);
    }

    function trapFocus(event) {
      var focusable = [prevBtn, nextBtn, closeBtn].filter(function (el) {
        return el && !el.disabled;
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (focusable.indexOf(document.activeElement) === -1) {
        event.preventDefault();
        first.focus();
      }
    }

    items.forEach(function (el, i) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        open(i);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        step(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        step(1);
      });
    }

    // Backdrop click (but not clicks on the image / caption / controls).
    lightbox.addEventListener('click', function (event) {
      if (event.target === lightbox || event.target.hasAttribute('data-gallery-backdrop')) close();
    });

    image.addEventListener('load', onImageLoad);

    lightbox.addEventListener(
      'touchstart',
      function (event) {
        if (!event.touches || event.touches.length !== 1) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      },
      { passive: true }
    );

    lightbox.addEventListener(
      'touchend',
      function (event) {
        if (!event.changedTouches || !event.changedTouches.length) return;
        var dx = event.changedTouches[0].clientX - touchStartX;
        var dy = event.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
        step(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );

    var api = {
      root: root,
      close: close,
      isOpen: function () {
        return isOpen;
      },
      handleKey: function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        } else if (event.key === 'Tab') {
          trapFocus(event);
        }
      },
    };

    return api;
  }

  function init(root) {
    if (!root || root.getAttribute('data-gallery-init') === 'true') return;
    var instance = GalleryGrid(root);
    if (!instance) return;
    root.setAttribute('data-gallery-init', 'true');
    instances.push(instance);
  }

  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches('[data-gallery-grid]')) init(context);
    Array.prototype.forEach.call(context.querySelectorAll('[data-gallery-grid]'), init);
  }

  document.addEventListener('keydown', function (event) {
    if (openInstance) openInstance.handleKey(event);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    instances = instances.filter(function (instance) {
      if (!event.target.contains(instance.root)) return true;
      if (instance.isOpen()) instance.close();
      return false;
    });
  });

  window.XenonGalleryGrid = { init: init, initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }
})();
